// src/primitives.ts
import { z } from "zod";
var INI_BREAKOUT = /[\r\n\0]/;
var MAX_INI_LINE_LENGTH = 255;
var iniValue = z.string().max(200).refine((v) => !INI_BREAKOUT.test(v), {
  message: "must not contain newlines or null bytes"
}).refine((v) => !/^\s*[;[]/.test(v), {
  message: 'must not start with ";" or "["'
});
function boundedIniValue(min, max) {
  return z.string().min(min).max(max).pipe(iniValue);
}
var iniSectionName = z.string().min(1).max(64).regex(/^[A-Za-z0-9_.-]+$/, 'may only contain letters, digits, "_", "." and "-"');
var dialNumber = z.string().min(2).max(11).regex(/^[0-9]+$/, "must be digits only");
var externalNumber = z.string().min(1).max(32).regex(/^\+?[0-9]+$/, 'must be digits, optionally prefixed with "+"');
var dtmfDigit = z.enum(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "#"]);
function normaliseDialPattern(value) {
  const trimmed = value.trim();
  if (trimmed.startsWith("_")) return trimmed;
  return /[XNZ.!\[\]]/i.test(trimmed) ? `_${trimmed}` : trimmed;
}
var dialPattern = z.string().trim().min(1).max(64).regex(/^_?[0-9NXZ*#+\[\]\-.!]+$/, "is not a valid Asterisk extension pattern").transform(normaliseDialPattern);
var uuid = z.string().uuid();
function isSafeIniLine(line) {
  return !INI_BREAKOUT.test(line) && line.length <= MAX_INI_LINE_LENGTH;
}

// src/destination.ts
import { z as z2 } from "zod";
var destination = z2.discriminatedUnion("type", [
  z2.object({ type: z2.literal("extension"), id: uuid }),
  z2.object({ type: z2.literal("ring_group"), id: uuid }),
  z2.object({ type: z2.literal("queue"), id: uuid }),
  z2.object({ type: z2.literal("ivr"), id: uuid }),
  z2.object({ type: z2.literal("time_condition"), id: uuid }),
  z2.object({ type: z2.literal("conference"), id: uuid }),
  z2.object({ type: z2.literal("park"), lotId: uuid }),
  z2.object({
    type: z2.literal("voicemail"),
    boxId: uuid,
    greeting: z2.enum(["unavail", "busy"]).default("unavail")
  }),
  z2.object({ type: z2.literal("announcement"), mediaId: uuid }),
  z2.object({ type: z2.literal("external"), number: externalNumber, routeId: uuid.optional() }),
  z2.object({ type: z2.literal("hangup"), cause: z2.number().int().min(1).max(127).optional() }),
  /** Announce to a group of phones that answer themselves. */
  z2.object({ type: z2.literal("paging"), id: uuid })
]);
var destinationTypes = destination.options.map(
  (option) => option.shape.type.value
);
function destinationToRow(d) {
  switch (d.type) {
    case "extension":
    case "ring_group":
    case "queue":
    case "ivr":
    case "time_condition":
    case "conference":
    case "paging":
      return { destinationType: d.type, destinationId: d.id, destinationValue: null };
    case "park":
      return { destinationType: d.type, destinationId: d.lotId, destinationValue: null };
    case "voicemail":
      return { destinationType: d.type, destinationId: d.boxId, destinationValue: d.greeting };
    case "announcement":
      return { destinationType: d.type, destinationId: d.mediaId, destinationValue: null };
    case "external":
      return {
        destinationType: d.type,
        destinationId: d.routeId ?? null,
        destinationValue: d.number
      };
    case "hangup":
      return {
        destinationType: d.type,
        destinationId: null,
        destinationValue: d.cause == null ? null : String(d.cause)
      };
  }
}
function destinationFromRow(row) {
  const { destinationType: type, destinationId: id, destinationValue: value } = row;
  switch (type) {
    case "extension":
    case "ring_group":
    case "queue":
    case "ivr":
    case "time_condition":
    case "conference":
    case "paging":
      return destination.parse({ type, id });
    case "park":
      return destination.parse({ type, lotId: id });
    case "voicemail":
      return destination.parse({ type, boxId: id, greeting: value ?? "unavail" });
    case "announcement":
      return destination.parse({ type, mediaId: id });
    case "external":
      return destination.parse({ type, number: value, ...id ? { routeId: id } : {} });
    case "hangup":
      return destination.parse({ type, ...value ? { cause: Number(value) } : {} });
  }
}
function destinationKey(d) {
  const row = destinationToRow(d);
  return `${row.destinationType}:${row.destinationId ?? row.destinationValue ?? ""}`;
}
function isTerminal(d) {
  return d.type === "hangup" || d.type === "external";
}
var MAX_FLOW_HOPS = 20;

// src/errors.ts
var ErrorCode = {
  OK: 0,
  // Common: -1..-99
  UNKNOWN: -1,
  NETWORK: -2,
  TIMEOUT: -3,
  INVALID_PARAMS: -4,
  DESTROYED: -5,
  NOT_SUPPORTED: -6,
  // PBX: -100..-199
  INVALID_SIGNATURE: -101,
  SIGNATURE_EXPIRED: -102,
  SIGNATURE_ALREADY_USED: -103,
  EXTENSION_DISABLED: -104,
  RATE_LIMITED: -105,
  PERMISSION_DENIED: -106,
  CONTROL_AUTH_FAILED: -107,
  CREDENTIALS_ROTATED: -108,
  LOGGED_IN_ELSEWHERE: -109,
  SERVER_UNAVAILABLE: -110,
  // Phone: -200..-299
  MEDIA_PERMISSION_DENIED: -201,
  NO_AUDIO_DEVICE: -202,
  ICE_FAILED: -203,
  REGISTRATION_FAILED: -204,
  CALL_REJECTED: -205,
  NO_SUCH_DESTINATION: -206,
  BUSY: -207,
  TRANSFER_FAILED: -208,
  RENEGOTIATION_FAILED: -209,
  INSECURE_CONTEXT: -210,
  MAX_CALLS_REACHED: -211,
  NO_SUCH_CALL: -212,
  // Conference: -300..-399 (our band; the reference API has no equivalent)
  CONFERENCE_FULL: -301,
  CONFERENCE_NOT_FOUND: -302,
  NOT_CONFERENCE_ADMIN: -303,
  ALREADY_IN_CONFERENCE: -304
};
function categoryOf(code) {
  const m = Math.abs(code);
  if (m >= 300) return "conference";
  if (m >= 200) return "phone";
  if (m >= 100) return "pbx";
  return "common";
}
var RETRYABLE = /* @__PURE__ */ new Set([
  ErrorCode.NETWORK,
  ErrorCode.TIMEOUT,
  ErrorCode.SERVER_UNAVAILABLE,
  ErrorCode.RATE_LIMITED,
  ErrorCode.ICE_FAILED
]);
function isRetryable(code) {
  return RETRYABLE.has(code);
}
var KlinkError = class extends Error {
  code;
  category;
  retryable;
  details;
  constructor(code, message, options = {}) {
    super(message, options.cause === void 0 ? void 0 : { cause: options.cause });
    this.name = "KlinkError";
    this.code = code;
    this.category = categoryOf(code);
    this.retryable = isRetryable(code);
    this.details = options.details;
  }
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      category: this.category,
      ...this.details === void 0 ? {} : { details: this.details }
    };
  }
};

// src/ws.ts
import { z as z3 } from "zod";
var WS_PROTOCOL_VERSION = 1;
var WS_PING_INTERVAL_MS = 25e3;
var WS_PONG_GRACE_MS = 1e4;
var WS_REPLAY_BUFFER_EVENTS = 500;
var WS_REPLAY_BUFFER_MS = 6e4;
var wsTopic = z3.enum(["call", "queue", "presence", "voicemail", "account", "system"]);
var wsClientMethod = z3.enum(["subscribe", "unsubscribe", "resume", "sync", "command"]);
var errorPayload = z3.object({
  code: z3.number().int(),
  message: z3.string(),
  details: z3.unknown().optional()
});
var wsEnvelope = z3.object({
  v: z3.literal(WS_PROTOCOL_VERSION),
  id: z3.string().min(1),
  type: z3.enum(["event", "req", "res", "ping", "pong"]),
  ts: z3.number().int(),
  topic: wsTopic.optional(),
  event: z3.string().optional(),
  method: z3.string().optional(),
  /** Set on `res` messages: the id of the `req` being answered. */
  ref: z3.string().optional(),
  seq: z3.number().int().optional(),
  data: z3.unknown().optional(),
  error: errorPayload.optional()
});
var WS_EVENTS = {
  call: [
    "call.ringing",
    "call.progress",
    "call.answered",
    "call.held",
    "call.resumed",
    "call.transferred",
    "call.recording",
    "call.ended"
  ],
  queue: [
    "queue.joined",
    "queue.position",
    "queue.offered",
    "queue.answered",
    "queue.abandoned",
    "queue.agent"
  ],
  presence: ["presence.changed"],
  voicemail: ["voicemail.count", "voicemail.new"],
  account: [
    "account.credentials.rotated",
    "account.session.superseded",
    "account.permissions.changed"
  ],
  system: ["system.maintenance"]
};
var subscribeRequest = z3.object({ topics: z3.array(wsTopic).min(1) });
var subscribeResponse = z3.object({
  granted: z3.array(wsTopic),
  denied: z3.array(wsTopic)
});
var resumeRequest = z3.object({
  sessionId: z3.string().min(1),
  lastSeq: z3.number().int().nonnegative()
});
var resumeResponse = z3.object({
  resumed: z3.boolean(),
  /** Present when `resumed` is false: the client must call `sync` to rebuild its view. */
  reason: z3.enum(["buffer_expired", "unknown_session", "seq_ahead"]).optional()
});

// src/pattern.ts
var NO_MATCH = { matched: false, specificity: 0 };
function normalizeNumber(input) {
  return input.replace(/[\s()\-.]/g, "");
}
function isPattern(pattern) {
  return pattern.startsWith("_");
}
function matchPattern(pattern, number) {
  const target = normalizeNumber(number);
  if (!isPattern(pattern)) {
    return pattern.toUpperCase() === target.toUpperCase() ? { matched: true, specificity: 1e6 } : NO_MATCH;
  }
  return matchBody(pattern.slice(1), target, 0, 0, 0);
}
function matchBody(pattern, target, pi, ti, specificity) {
  if (pi >= pattern.length) {
    return ti === target.length ? { matched: true, specificity } : NO_MATCH;
  }
  const pc = pattern[pi];
  if (pc === ".") {
    for (let take = target.length - ti; take >= 1; take--) {
      const result = matchBody(pattern, target, pi + 1, ti + take, specificity + 1);
      if (result.matched) return result;
    }
    return NO_MATCH;
  }
  if (pc === "!") {
    for (let take = target.length - ti; take >= 0; take--) {
      const result = matchBody(pattern, target, pi + 1, ti + take, specificity);
      if (result.matched) return result;
    }
    return NO_MATCH;
  }
  if (ti >= target.length) return NO_MATCH;
  const tc = target[ti];
  if (pc === "[") {
    const close = pattern.indexOf("]", pi);
    if (close === -1) return NO_MATCH;
    const set = pattern.slice(pi + 1, close);
    return matchesSet(set, tc) ? matchBody(pattern, target, close + 1, ti + 1, specificity + 100) : NO_MATCH;
  }
  const classSpecificity = matchesClass(pc, tc);
  if (classSpecificity === null) return NO_MATCH;
  return matchBody(pattern, target, pi + 1, ti + 1, specificity + classSpecificity);
}
function matchesClass(pc, tc) {
  switch (pc.toUpperCase()) {
    case "X":
      return tc >= "0" && tc <= "9" ? 10 : null;
    case "Z":
      return tc >= "1" && tc <= "9" ? 20 : null;
    case "N":
      return tc >= "2" && tc <= "9" ? 30 : null;
    default:
      return pc.toUpperCase() === tc.toUpperCase() ? 1e3 : null;
  }
}
function matchesSet(set, tc) {
  for (let i = 0; i < set.length; i++) {
    const c = set[i];
    if (set[i + 1] === "-" && i + 2 < set.length) {
      const end = set[i + 2];
      if (tc >= c && tc <= end) return true;
      i += 2;
      continue;
    }
    if (c === tc) return true;
  }
  return false;
}
function bestMatch(routes, number) {
  let best = null;
  for (const route of routes) {
    const match = matchPattern(route.pattern, number);
    if (!match.matched) continue;
    if (best === null || route.priority < best.route.priority || route.priority === best.route.priority && match.specificity > best.match.specificity) {
      best = { route, match };
    }
  }
  return best;
}
function transformNumber(number, strip, prepend) {
  const stripped = normalizeNumber(number).slice(Math.max(0, strip));
  return `${prepend ?? ""}${stripped}`;
}

// src/sip-cause.ts
var SIP_TO_Q931 = {
  401: 21,
  // unauthorized -> call rejected
  403: 21,
  // forbidden
  404: 1,
  // not found -> unallocated number
  405: 79,
  // method not allowed -> service not implemented
  407: 21,
  // proxy auth required
  408: 102,
  // request timeout -> recovery on timer expiry
  410: 22,
  // gone -> number changed
  413: 127,
  // request entity too large -> interworking
  414: 127,
  416: 127,
  420: 127,
  421: 127,
  423: 127,
  480: 18,
  // temporarily unavailable -> no user responding
  483: 18,
  // too many hops
  484: 28,
  // address incomplete -> invalid number format
  486: 17,
  // busy here -> user busy
  488: 58,
  // not acceptable here -> bearer capability unavailable
  500: 41,
  // server internal error -> temporary failure
  501: 79,
  // not implemented
  502: 38,
  // bad gateway -> network out of order
  503: 41,
  // service unavailable -> temporary failure
  504: 102,
  // gateway timeout
  505: 127,
  // version not supported
  513: 127,
  // message too large
  600: 17,
  // busy everywhere
  603: 21,
  // decline
  604: 1,
  // does not exist anywhere
  606: 58
  // not acceptable
};
var NEVER_FAILOVER = /* @__PURE__ */ new Set([16, 17]);
function failoverCauses(sipCodes) {
  const out = /* @__PURE__ */ new Set();
  for (const code of sipCodes) {
    const cause = SIP_TO_Q931[code];
    if (cause !== void 0 && !NEVER_FAILOVER.has(cause)) out.add(cause);
  }
  return out;
}
function undetectableSipCodes(sipCodes) {
  return sipCodes.filter((code) => {
    const cause = SIP_TO_Q931[code];
    return cause === void 0 || NEVER_FAILOVER.has(cause);
  });
}
function supportedFailoverCodes() {
  return Object.keys(SIP_TO_Q931).map(Number).filter((code) => !NEVER_FAILOVER.has(SIP_TO_Q931[code])).sort((a, b) => a - b);
}

// src/device.ts
var DEVICE_KINDS = ["desk", "desktop", "mobile", "web"];
var DEVICE_LABELS = {
  desk: "Desk phone",
  desktop: "Desktop",
  mobile: "Mobile",
  web: "Web"
};
var DESK_VENDORS = [
  "yealink",
  "grandstream",
  "fanvil",
  "polycom",
  "poly",
  "snom",
  "cisco",
  "htek",
  "akuvox",
  "gigaset",
  "panasonic",
  "aastra",
  "mitel",
  "alcatel",
  "escene",
  "dinstar",
  "flyingvoice",
  "vtech",
  "obihai",
  "linkvil"
];
var MOBILE_HINTS = [
  "iphone",
  "ipad",
  "ios",
  "android",
  "linphone-ios",
  "linphoneandroid",
  "bria mobile",
  "groundwire",
  "acrobits",
  "zoiper for"
];
function classifyContact(endpoint, userAgent) {
  if (endpoint.endsWith("_wrtc")) return "web";
  const ua = (userAgent ?? "").toLowerCase();
  if (ua === "") return "desktop";
  if (MOBILE_HINTS.some((hint) => ua.includes(hint))) return "mobile";
  if (DESK_VENDORS.some((vendor) => ua.includes(vendor))) return "desk";
  return "desktop";
}
function contactAddress(uri, fallback) {
  if (!uri) return fallback;
  const at = uri.lastIndexOf("@");
  if (at === -1) return fallback;
  let host = uri.slice(at + 1);
  for (const separator of ["^3B", ";", ">", ","]) {
    const index = host.indexOf(separator);
    if (index !== -1) host = host.slice(0, index);
  }
  if (host.startsWith("[")) {
    const close = host.indexOf("]");
    return close === -1 ? fallback : host.slice(1, close);
  }
  const colon = host.indexOf(":");
  const address = colon === -1 ? host : host.slice(0, colon);
  return address.length > 0 ? address : fallback;
}
export {
  DEVICE_KINDS,
  DEVICE_LABELS,
  ErrorCode,
  KlinkError,
  MAX_FLOW_HOPS,
  MAX_INI_LINE_LENGTH,
  WS_EVENTS,
  WS_PING_INTERVAL_MS,
  WS_PONG_GRACE_MS,
  WS_PROTOCOL_VERSION,
  WS_REPLAY_BUFFER_EVENTS,
  WS_REPLAY_BUFFER_MS,
  bestMatch,
  boundedIniValue,
  categoryOf,
  classifyContact,
  contactAddress,
  destination,
  destinationFromRow,
  destinationKey,
  destinationToRow,
  destinationTypes,
  dialNumber,
  dialPattern,
  dtmfDigit,
  externalNumber,
  failoverCauses,
  iniSectionName,
  iniValue,
  isPattern,
  isRetryable,
  isSafeIniLine,
  isTerminal,
  matchPattern,
  normaliseDialPattern,
  normalizeNumber,
  resumeRequest,
  resumeResponse,
  subscribeRequest,
  subscribeResponse,
  supportedFailoverCodes,
  transformNumber,
  undetectableSipCodes,
  uuid,
  wsClientMethod,
  wsEnvelope,
  wsTopic
};
//# sourceMappingURL=index.js.map